import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactFlow, {
  Background, BackgroundVariant, Controls, MiniMap, Panel, MarkerType, Position, useNodesState, useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTranslation } from 'react-i18next';
import { getNearFlow, getTreasuryWatchCases, getTreasuryWatchCase } from '../api';

const CIRCLE_SIZE = 40;
const RADIAL_STEP = 150;
const MIN_ARC_GAP = CIRCLE_SIZE + 24;
const FAN_SPAN = (300 * Math.PI) / 180;
const FAN_START = (-150 * Math.PI) / 180;
const LAST_SEARCH_KEY = 'tf_nearflow_last';

// Edge type -> color + arrow semantics. `withdraw` gets its own bright color
// since it's the one event type that means funds ACTUALLY moved out of a
// staking pool back to a wallet — everything staking-related upstream of it
// (unstake/unstake_all) is only a pending request. See the Staking feature's
// known gap: it tracks unstake requests, not this withdraw completion event.
const EDGE_STYLES = {
  transfer:    { color: '#38bdf8', labelKey: 'nearFlow.edgeTransfer' },
  stake:       { color: '#10b981', labelKey: 'nearFlow.edgeStake' },
  unstake:     { color: '#f59e0b', labelKey: 'nearFlow.edgeUnstake' },
  withdraw:    { color: '#dc2626', labelKey: 'nearFlow.edgeWithdraw' },
  ft_transfer: { color: '#a78bfa', labelKey: 'nearFlow.edgeFtTransfer' },
};

function fmtNear(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(2);
}

function todayIso() { return new Date().toISOString().slice(0, 10); }
function monthsAgoIso(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}

function parseSeeds(input) {
  return [...new Set(String(input || '').split(';').map((s) => s.trim()).filter(Boolean))];
}

// Same sunburst radial layout as the EVM/Nansen Flow Graph (see
// FlowGraphPage.jsx) — kept in sync deliberately so a user familiar with one
// tool immediately reads the other. Adapted for string NEAR account ids
// instead of 0x addresses; no meta-node grouping since there's no label
// service to group by.
function layoutNodes(visibleNodes, visibleEdges) {
  const byAcct = new Map(visibleNodes.map((n) => [n.account, n]));
  const hopOf = (a) => byAcct.get(a)?.hop;
  const seedAccts = visibleNodes.filter((n) => n.hop === 0).map((n) => n.account);
  if (seedAccts.length === 0) return [];

  const parentOf = new Map();
  const bestSeen = new Map();
  for (const e of visibleEdges) {
    const hopA = hopOf(e.from);
    const hopB = hopOf(e.to);
    if (hopA == null || hopB == null) continue;
    let parent, child;
    if (hopA === hopB - 1) { parent = e.from; child = e.to; }
    else if (hopB === hopA - 1) { parent = e.to; child = e.from; }
    else continue;
    if (!bestSeen.has(child)) { bestSeen.set(child, true); parentOf.set(child, parent); }
  }

  const children = new Map();
  for (const n of visibleNodes) {
    if (n.hop === 0) continue;
    const p = parentOf.get(n.account) ?? seedAccts[0];
    if (!children.has(p)) children.set(p, []);
    children.get(p).push(n.account);
  }

  const weight = new Map();
  function subtreeWeight(acct) {
    const kids = children.get(acct) || [];
    const w = kids.length === 0 ? 1 : kids.reduce((sum, k) => sum + subtreeWeight(k), 0);
    weight.set(acct, w);
    return w;
  }
  seedAccts.forEach(subtreeWeight);

  const angleByAcct = new Map();
  function assignAngles(acct, start, end) {
    angleByAcct.set(acct, (start + end) / 2);
    const kids = children.get(acct) || [];
    if (kids.length === 0) return;
    const totalW = kids.reduce((sum, k) => sum + (weight.get(k) || 1), 0) || kids.length;
    let cursor = start;
    for (const k of kids) {
      const span = (end - start) * ((weight.get(k) || 1) / totalW);
      assignAngles(k, cursor, cursor + span);
      cursor += span;
    }
  }

  if (seedAccts.length === 1) {
    assignAngles(seedAccts[0], FAN_START, FAN_START + FAN_SPAN);
  } else {
    const totalSeedWeight = seedAccts.reduce((sum, a) => sum + (weight.get(a) || 1), 0);
    let cursor = -Math.PI;
    for (const seedAcct of seedAccts) {
      const span = 2 * Math.PI * ((weight.get(seedAcct) || 1) / totalSeedWeight);
      assignAngles(seedAcct, cursor, cursor + span);
      cursor += span;
    }
  }

  const SEED_RING_RADIUS = seedAccts.length > 1 ? 70 : 0;
  const byHop = new Map();
  for (const n of visibleNodes) {
    if (!byHop.has(n.hop)) byHop.set(n.hop, []);
    byHop.get(n.hop).push(n);
  }

  const fullSweep = seedAccts.length > 1 ? 2 * Math.PI : FAN_SPAN;
  const positioned = [];
  for (const [hop, group] of byHop) {
    const angles = group.map((n) => angleByAcct.get(n.account) ?? 0).sort((a, b) => a - b);
    let minGap = Infinity;
    for (let i = 0; i < angles.length - 1; i++) minGap = Math.min(minGap, angles[i + 1] - angles[i]);
    if (!Number.isFinite(minGap)) minGap = fullSweep;
    const baseRadius = SEED_RING_RADIUS + hop * RADIAL_STEP;
    const radius = Math.max(baseRadius, MIN_ARC_GAP / Math.max(minGap, 0.001));
    group.forEach((n) => {
      const angle = angleByAcct.get(n.account) ?? 0;
      positioned.push({ ...n, x: radius * Math.cos(angle), y: radius * Math.sin(angle), angle });
    });
  }
  return positioned;
}

function radialHandles(angle) {
  const deg = ((angle * 180) / Math.PI + 360) % 360;
  if (deg >= 315 || deg < 45) return { outward: Position.Right, inward: Position.Left };
  if (deg >= 45 && deg < 135) return { outward: Position.Bottom, inward: Position.Top };
  if (deg >= 135 && deg < 225) return { outward: Position.Left, inward: Position.Right };
  return { outward: Position.Top, inward: Position.Bottom };
}

// Pool/validator contracts follow a handful of well-known naming
// conventions (poolv1, .pool., lockup) — flagged visually since a hop
// landing on one of these is usually the interesting part of a staking trace.
function isPoolLike(account) {
  return /\.(poolv1|pool)\.near$|lockup/i.test(account || '');
}

// Saved cases (see ROLE_COLOR) carry an explicit `role` from curation — takes
// priority over the hop/pool-name heuristics used for a live trace, since a
// human already classified these nodes.
const ROLE_COLOR = {
  origin: '#7c3aed', validator_pool: '#f59e0b', hub: '#dc2626',
  satellite: '#10b981', relay: '#0ea5e9', accumulator: '#ec4899', source: '#6b7280',
};

function nodeColor(node) {
  if (node.role && ROLE_COLOR[node.role]) {
    const c = ROLE_COLOR[node.role];
    return { border: c, glow: `${c}99` };
  }
  if (node.hop === 0) return { border: '#7c3aed', glow: '#7c3aed99' };
  if (isPoolLike(node.account)) return { border: '#f59e0b', glow: '#f59e0b99' };
  return { border: '#10b981', glow: '#10b98199' };
}

function AccountNode({ n }) {
  const [hover, setHover] = useState(false);
  const { border, glow } = nodeColor(n);
  const label = n.role === 'hub' ? 'HUB' : n.hop === 0 ? 'SEED' : (n.account || '').slice(0, 4).toUpperCase();
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: CIRCLE_SIZE, height: CIRCLE_SIZE, borderRadius: '50%',
        background: `radial-gradient(circle at 35% 30%, ${border}33, #05070d 78%)`,
        border: `1.5px solid ${border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 8, fontWeight: 800, color: '#e8ecf5', fontFamily: 'monospace',
        cursor: 'pointer',
        boxShadow: hover ? `0 0 14px 3px ${glow}, 0 0 0 3px ${border}55` : `0 0 8px 1px ${glow}`,
        transition: 'box-shadow 0.15s',
      }}
    >
      {label}
    </div>
  );
}

const LEGEND_ITEMS = [
  { color: '#7c3aed', key: 'nearFlow.legendSeed' },
  { color: '#f59e0b', key: 'nearFlow.legendPool' },
  { color: '#10b981', key: 'nearFlow.legendAccount' },
];

function GraphLegend() {
  const { t } = useTranslation();
  return (
    <div style={{
      background: 'linear-gradient(180deg, #0d1220, #080b14)', border: '1px solid #2a3352',
      borderRadius: 10, padding: '8px 10px', fontSize: 10, color: '#c3c9dc',
      boxShadow: '0 8px 28px rgba(0,0,0,0.5)', pointerEvents: 'none',
    }}>
      {LEGEND_ITEMS.map((it) => (
        <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: it.color, boxShadow: `0 0 5px ${it.color}` }} />
          {t(it.key)}
        </div>
      ))}
      <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #22283e' }}>
        {Object.entries(EDGE_STYLES).map(([type, cfg]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <span style={{ width: 12, height: 2, background: cfg.color, boxShadow: `0 0 4px ${cfg.color}` }} />
            {t(cfg.labelKey)}
          </div>
        ))}
      </div>
    </div>
  );
}

function CopyableText({ text, copied, onCopy }) {
  const { t } = useTranslation();
  return (
    <div
      onClick={() => onCopy(text)}
      title={t('flowGraph.clickToCopy')}
      style={{ fontSize: 10, color: copied ? '#22d3a8' : '#9aa4bf', fontFamily: 'monospace', wordBreak: 'break-all', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
    >
      {copied ? t('flowGraph.copied') : text}
      {!copied && <span style={{ fontSize: 9, flexShrink: 0 }}>📋</span>}
    </div>
  );
}

function PinnedTooltip({ node, edgesTouching, onClose }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [node]);
  if (!node) return null;

  const copy = () => {
    navigator.clipboard.writeText(node.account).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  return (
    <div style={{
      background: 'linear-gradient(180deg, #0d1220, #080b14)', border: '1px solid #2a3352',
      borderRadius: 10, padding: '10px 12px', minWidth: 240, maxWidth: 300,
      boxShadow: '0 8px 28px rgba(0,0,0,0.55)', textAlign: 'left', pointerEvents: 'auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {node.role && <div style={{ fontSize: 9, fontWeight: 800, color: ROLE_COLOR[node.role] || '#a78bfa', marginBottom: 4 }}>{t(`treasuryWatch.role.${node.role}`)}</div>}
          {!node.role && node.hop === 0 && <div style={{ fontSize: 9, fontWeight: 800, color: '#a78bfa', marginBottom: 4 }}>SEED</div>}
          {!node.role && isPoolLike(node.account) && <div style={{ fontSize: 9, fontWeight: 800, color: '#f59e0b', marginBottom: 4 }}>{t('nearFlow.poolBadge')}</div>}
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#6b7492', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 2, flexShrink: 0 }}>✕</button>
      </div>
      <CopyableText text={node.account} copied={copied} onCopy={copy} />
      {node.label && <div style={{ fontSize: 10, color: '#c3c9dc', marginTop: 4 }}>{node.label}</div>}
      {node.currentBalanceNear != null && (
        <div style={{ fontSize: 10, color: '#8891ac', marginTop: 4 }}>{t('treasuryWatch.currentBalance')}: <strong style={{ color: '#e8ecf5' }}>{fmtNear(node.currentBalanceNear)} NEAR</strong></div>
      )}
      {node.role == null && <div style={{ fontSize: 9, color: '#6b7492', marginTop: 4 }}>{t('flowGraph.hopLabel', { hop: node.hop })}</div>}
      {edgesTouching.length > 0 && (
        <div style={{ marginTop: 8, borderTop: '1px solid #22283e', paddingTop: 6, maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {edgesTouching.map((e, i) => (
            <a key={i} href={`https://nearblocks.io/txns/${e.txHash}`} target="_blank" rel="noreferrer"
              style={{ fontSize: 9.5, color: EDGE_STYLES[e.type]?.color || '#c3c9dc', textDecoration: 'none' }}>
              {e.from === node.account ? '→' : '←'} {t(EDGE_STYLES[e.type]?.labelKey || 'nearFlow.edgeOther')}
              {e.amountNear != null ? ` ${fmtNear(e.amountNear)} NEAR` : ''}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function edgeWidth() { return 2; }

function buildFlowElements(graph, focusAccount) {
  if (!graph) return { nodes: [], edges: [] };

  const adjacency = new Map();
  const link = (a, b) => { if (!adjacency.has(a)) adjacency.set(a, new Set()); adjacency.get(a).add(b); };
  graph.edges.forEach((e) => { link(e.from, e.to); link(e.to, e.from); });

  const seedNodes = graph.nodes.filter((n) => n.hop === 0);
  const seedSet = new Set(seedNodes.map((n) => n.account));
  const reachable = new Set();
  const queue = [];
  for (const sn of seedNodes) { reachable.add(sn.account); queue.push(sn.account); }
  while (queue.length) {
    const cur = queue.shift();
    for (const next of adjacency.get(cur) || []) {
      if (!reachable.has(next)) { reachable.add(next); queue.push(next); }
    }
  }

  let visibleNodes = graph.nodes.filter((n) => reachable.has(n.account));
  let visibleEdges = graph.edges.filter((e) => reachable.has(e.from) && reachable.has(e.to));

  if (focusAccount && !seedSet.has(focusAccount)) {
    const related = new Set([...seedSet, focusAccount]);
    visibleEdges.forEach((e) => {
      if (e.from === focusAccount) related.add(e.to);
      if (e.to === focusAccount) related.add(e.from);
    });
    visibleNodes = visibleNodes.filter((n) => related.has(n.account));
    visibleEdges = visibleEdges.filter((e) => related.has(e.from) && related.has(e.to));
  }

  const positioned = layoutNodes(visibleNodes, visibleEdges);

  const nodes = positioned.map((n) => {
    const { outward, inward } = radialHandles(n.angle || 0);
    return {
      id: n.account,
      position: { x: n.x, y: n.y },
      sourcePosition: outward,
      targetPosition: inward,
      data: { label: <AccountNode n={n} />, minimapColor: nodeColor(n).border, raw: n },
      style: { background: 'transparent', border: 'none', padding: 0, width: CIRCLE_SIZE, height: CIRCLE_SIZE },
    };
  });

  const hopByAcct = new Map(visibleNodes.map((n) => [n.account, n.hop]));
  const edges = visibleEdges.map((e, i) => {
    const hopFrom = hopByAcct.get(e.from) ?? 0;
    const hopTo = hopByAcct.get(e.to) ?? 0;
    const geomInner = hopFrom <= hopTo ? e.from : e.to;
    const geomOuter = hopFrom <= hopTo ? e.to : e.from;
    const destinationIsInner = e.to === geomInner;
    const style = EDGE_STYLES[e.type] || { color: '#4b5563' };
    return {
      id: `e${i}-${e.from}-${e.to}-${e.txHash}`,
      source: geomInner,
      target: geomOuter,
      type: 'straight',
      label: e.amountNear != null ? `${fmtNear(e.amountNear)} NEAR` : (e.method || e.type),
      labelStyle: { fontSize: 9, fill: '#c3c9dc', fontWeight: 600 },
      labelBgStyle: { fill: '#0b0f1a', fillOpacity: 0.85 },
      style: { stroke: style.color, strokeWidth: edgeWidth(), filter: `drop-shadow(0 0 3px ${style.color}aa)` },
      markerStart: destinationIsInner ? { type: MarkerType.ArrowClosed, color: style.color } : undefined,
      markerEnd: destinationIsInner ? undefined : { type: MarkerType.ArrowClosed, color: style.color },
      animated: false,
    };
  });

  const visibleHopCounts = {};
  visibleNodes.forEach((n) => { visibleHopCounts[n.hop] = (visibleHopCounts[n.hop] || 0) + 1; });
  const rawHopCounts = {};
  graph.nodes.forEach((n) => { rawHopCounts[n.hop] = (rawHopCounts[n.hop] || 0) + 1; });

  return { nodes, edges, visibleHopCounts, rawHopCounts, rawEdges: visibleEdges };
}

// A saved TreasuryWatchCase has no BFS `hop` — computed here via plain
// distance-from-origin over the case's own edges, purely so the existing
// hop-based radial layout (built for live traces) also works for curated
// cases without a second layout algorithm.
function assignHopsFromOrigin(nodes, edges) {
  const originAccount = (nodes.find((n) => n.role === 'origin') || nodes.find((n) => n.role === 'hub') || nodes[0])?.account;
  const adjacency = new Map();
  const link = (a, b) => { if (!adjacency.has(a)) adjacency.set(a, new Set()); adjacency.get(a).add(b); };
  edges.forEach((e) => { link(e.from, e.to); link(e.to, e.from); });
  const hopByAccount = new Map([[originAccount, 0]]);
  const queue = [originAccount];
  while (queue.length) {
    const cur = queue.shift();
    for (const next of adjacency.get(cur) || []) {
      if (!hopByAccount.has(next)) { hopByAccount.set(next, hopByAccount.get(cur) + 1); queue.push(next); }
    }
  }
  return nodes.map((n) => ({ ...n, hop: hopByAccount.get(n.account) ?? 0 }));
}

// Reshapes a TreasuryWatchCase into the same {nodes,edges,nodeCount,...}
// shape a live trace produces, so the entire render pipeline below
// (buildFlowElements, layoutNodes, nodeColor, tooltip) is shared unchanged.
function caseToGraph(caseData) {
  const nodes = assignHopsFromOrigin(caseData.nodes, caseData.edges);
  const edges = caseData.edges.map((e) => ({ ...e, type: e.type || 'transfer' }));
  return {
    nodes, edges, nodeCount: nodes.length, edgeCount: edges.length,
    cached: true, truncated: false, fetchedAt: caseData.updatedAt,
    totalNearHeld: caseData.totalNearHeld, title: caseData.title, description: caseData.description,
  };
}

export default function NearFlowPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState('saved'); // 'saved' | 'live' — saved cases don't touch NearBlocks at all
  const [seedInput, setSeedInput] = useState('');
  const [depth, setDepth] = useState(2);
  const [from, setFrom] = useState(monthsAgoIso(3));
  const [to, setTo] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [graph, setGraph] = useState(null);
  const [hopStats, setHopStats] = useState(null);
  const [rawEdges, setRawEdges] = useState([]);
  const [focusAccount, setFocusAccount] = useState(null);
  const [activeNode, setActiveNode] = useState(null);

  const [cases, setCases] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Saved-case list, loaded once — auto-selects the first (most recent) case.
  useEffect(() => {
    let cancelled = false;
    getTreasuryWatchCases()
      .then((list) => {
        if (cancelled) return;
        setCases(list);
        if (list.length > 0) setSelectedSlug(list[0].slug);
      })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (mode !== 'saved' || !selectedSlug) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    getTreasuryWatchCase(selectedSlug)
      .then((caseData) => { if (!cancelled) { setFocusAccount(null); setActiveNode(null); setGraph(caseToGraph(caseData)); } })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mode, selectedSlug]);

  useEffect(() => {
    if (mode !== 'live') return;
    try {
      const raw = localStorage.getItem(LAST_SEARCH_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved || !saved.graph) return;
      setSeedInput(saved.seedInput || '');
      setDepth(saved.depth || 2);
      setFrom(saved.from || monthsAgoIso(3));
      setTo(saved.to || todayIso());
      setGraph(saved.graph);
    } catch { /* corrupt/old entry — ignore */ }
  }, [mode]);

  useEffect(() => {
    if (!graph) { setNodes([]); setEdges([]); setHopStats(null); setRawEdges([]); return; }
    const { nodes: n, edges: e, visibleHopCounts, rawHopCounts, rawEdges: re } = buildFlowElements(graph, focusAccount);
    setNodes(n);
    setEdges(e);
    setHopStats({ visibleHopCounts, rawHopCounts });
    setRawEdges(re);
  }, [graph, focusAccount, setNodes, setEdges]);

  const runLoad = useCallback(async (seedsArr, depthVal, fromVal, toVal, forceRefresh = false) => {
    if (!seedsArr || seedsArr.length === 0) { setError(t('nearFlow.errNoAccountInput')); return; }
    setLoading(true);
    setError('');
    try {
      const result = await getNearFlow({ seeds: seedsArr, depth: depthVal, from: fromVal, to: toVal, forceRefresh });
      setFocusAccount(null);
      setActiveNode(null);
      setGraph(result);
      try {
        localStorage.setItem(LAST_SEARCH_KEY, JSON.stringify({
          seedInput: seedsArr.join('; '), depth: depthVal, from: fromVal, to: toVal, graph: result,
        }));
      } catch { /* quota exceeded — non-fatal */ }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const load = useCallback((forceRefresh = false) => {
    runLoad(parseSeeds(seedInput), depth, from, to, forceRefresh);
  }, [seedInput, depth, from, to, runLoad]);

  const handleNodeDoubleClick = useCallback((_event, node) => {
    const isSeed = graph?.nodes.some((n) => n.hop === 0 && n.account === node.id);
    setFocusAccount(isSeed ? null : node.id);
  }, [graph]);

  const handleNodeClick = useCallback((_event, node) => {
    if (node.data?.raw) setActiveNode(node.data.raw);
  }, []);

  const activeNodeEdges = useMemo(() => {
    if (!activeNode) return [];
    return rawEdges.filter((e) => e.from === activeNode.account || e.to === activeNode.account);
  }, [activeNode, rawEdges]);

  const stats = useMemo(() => {
    if (!graph) return null;
    return {
      nodeCount: nodes.length, edgeCount: edges.length,
      rawNodeCount: graph.nodeCount, rawEdgeCount: graph.edgeCount,
      cached: graph.cached, truncated: graph.truncated, fetchedAt: graph.fetchedAt,
      fetchErrors: graph.fetchErrors || [], totalNearHeld: graph.totalNearHeld,
    };
  }, [graph, nodes, edges]);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>{t('nearFlow.title')}</h2>
        <p style={s.subtitle}>{t('nearFlow.subtitle')}</p>
      </div>

      <div style={s.modeToggle}>
        <button style={mode === 'saved' ? s.modeBtnActive : s.modeBtn} onClick={() => setMode('saved')}>
          {t('nearFlow.modeSaved')}
        </button>
        <button style={mode === 'live' ? s.modeBtnActive : s.modeBtn} onClick={() => setMode('live')}>
          {t('nearFlow.modeLive')}
        </button>
      </div>

      {mode === 'saved' ? (
        <div style={s.form}>
          <div style={{ ...s.field, flex: 1, minWidth: 260 }}>
            <span style={s.fieldLabel}>{t('nearFlow.fieldCase')}</span>
            <select style={s.input} value={selectedSlug} onChange={(e) => setSelectedSlug(e.target.value)}>
              {cases.map((c) => (
                <option key={c.slug} value={c.slug}>{c.title} ({c.nodeCount} ví)</option>
              ))}
            </select>
            {graph?.title && (
              <div style={s.caseHeading}>
                <div style={s.caseTitle}>{graph.title}</div>
                {graph.description && <div style={s.caseDescription}>{graph.description}</div>}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={s.form}>
          <div style={{ ...s.field, flex: 2, minWidth: 260 }}>
            <span style={s.fieldLabel}>{t('nearFlow.fieldAccounts')}</span>
            <input style={s.input} value={seedInput} onChange={(e) => setSeedInput(e.target.value)} placeholder={t('nearFlow.accountPlaceholder')} />
          </div>
          <div style={{ ...s.field, width: 80 }}>
            <span style={s.fieldLabel}>{t('flowGraph.fieldDepth')}</span>
            <select style={s.input} value={depth} onChange={(e) => setDepth(Number(e.target.value))}>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>
          <div style={s.field}>
            <span style={s.fieldLabel}>{t('flowGraph.fieldFromDate')}</span>
            <input style={s.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div style={s.field}>
            <span style={s.fieldLabel}>{t('flowGraph.fieldToDate')}</span>
            <input style={s.input} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button style={s.btnPrimary} onClick={() => load(false)} disabled={loading}>
            {loading ? t('flowGraph.btnTracing') : t('flowGraph.btnTrace')}
          </button>
          <button style={s.btnGhost} onClick={() => load(true)} disabled={loading} title={t('flowGraph.forceRefreshTitle')}>
            {t('flowGraph.btnForceRefresh')}
          </button>
        </div>
      )}
      {mode === 'live' && <div style={s.hint}>{t('nearFlow.liveModeWarning')}</div>}

      {error && <div style={s.errorBox}>{error}</div>}
      {stats?.fetchErrors?.length > 0 && (
        <div style={s.errorBox}>
          {t('nearFlow.fetchErrorsTitle')}
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {stats.fetchErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {stats && (
        <div style={s.statsRow}>
          <span style={s.statChip}>{t('flowGraph.statNodesShown', { shown: stats.nodeCount, total: stats.rawNodeCount })}</span>
          <span style={s.statChip}>{t('nearFlow.statEdgesShown', { shown: stats.edgeCount, total: stats.rawEdgeCount })}</span>
          {mode === 'saved' ? (
            stats.totalNearHeld != null && <span style={s.statChip}>{t('treasuryWatch.totalHeld', { amount: fmtNear(stats.totalNearHeld) })}</span>
          ) : (
            <span style={{ ...s.statChip, color: stats.cached ? '#10b981' : '#f59e0b' }}>
              {stats.cached ? t('flowGraph.statFromCache') : t('nearFlow.statFresh')}
            </span>
          )}
          {stats.truncated && <span style={{ ...s.statChip, color: '#dc2626' }}>{t('flowGraph.statTruncated')}</span>}
        </div>
      )}

      {hopStats && (
        <div style={s.statsRow}>
          <span style={s.statChip} title={t('flowGraph.byHopTitle')}>
            {t('flowGraph.byHopLabel', {
              summary: Object.keys({ ...hopStats.rawHopCounts, ...hopStats.visibleHopCounts })
                .map(Number).sort((a, b) => a - b)
                .map((hop) => `Hop ${hop}: ${hopStats.visibleHopCounts[hop] || 0}/${hopStats.rawHopCounts[hop] || 0}`)
                .join('  •  '),
            })}
          </span>
          {focusAccount && (
            <button style={s.btnGhost} onClick={() => setFocusAccount(null)} title={focusAccount}>
              {t('flowGraph.focusActive')}
            </button>
          )}
        </div>
      )}

      <div style={s.hint}>{t('nearFlow.withdrawHint')}</div>

      <div style={s.canvasWrap} className="tf-dark-flow">
        <style>{`
          .tf-dark-flow .react-flow__controls-button { background: #131a2b; border-bottom: 1px solid #232b45; fill: #c3c9dc; }
          .tf-dark-flow .react-flow__controls-button:hover { background: #1b2440; }
          .tf-dark-flow .react-flow__controls { border: 1px solid #232b45; border-radius: 8px; overflow: hidden; }
          .tf-dark-flow .react-flow__attribution { background: transparent; }
        `}</style>
        {nodes.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>🔗</div>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{t('nearFlow.emptyPrompt')}</div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            onNodeDoubleClick={handleNodeDoubleClick}
            fitView proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={22} size={1.4} color="#1e2740" />
            <Controls />
            <MiniMap
              pannable zoomable
              nodeColor={(n) => n.data?.minimapColor || '#4b5563'}
              nodeStrokeColor={(n) => n.data?.minimapColor || '#4b5563'}
              nodeStrokeWidth={2}
              nodeBorderRadius={8}
              maskColor="rgba(0,0,0,0.55)"
              style={{ background: '#0b0f1a', border: '1px solid #232b45' }}
            />
            <Panel position="top-left">
              <PinnedTooltip node={activeNode} edgesTouching={activeNodeEdges} onClose={() => setActiveNode(null)} />
            </Panel>
            <Panel position="bottom-left">
              <GraphLegend />
            </Panel>
          </ReactFlow>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto', display: 'flex', flexDirection: 'column' },
  header: { marginBottom: 16 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },

  modeToggle: { display: 'flex', gap: 6, marginBottom: 12 },
  modeBtn: { background: 'transparent', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  modeBtnActive: { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)', borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },

  form: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 130 },
  fieldLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', fontSize: 13, color: 'var(--text)' },
  btnPrimary: { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },

  errorBox: { background: '#dc262614', border: '1px solid #dc262655', color: '#dc2626', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 },

  statsRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  statChip: { fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px' },

  hint: { fontSize: 11.5, color: 'var(--text-muted)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 },

  caseHeading: { marginTop: 8, width: '100%' },
  caseTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text)' },
  caseDescription: { fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.5 },

  canvasWrap: { height: 750, background: '#05070d', border: '1px solid #232b45', borderRadius: 14, overflow: 'hidden', position: 'relative' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' },
};
